type MigrationCallbacks = {
  current: () => WebSocket | null
  open: () => WebSocket
  auth: () => Record<string, unknown>
  promote: (socket: WebSocket) => void
  receive: (data: any) => void
  failed: (message: string) => void
}
export function parseSocketFrame(raw: string): any {
  try {
    const parsed = JSON.parse(raw)

    return parsed && typeof parsed === 'object' && (parsed.type || parsed.update) ? parsed : raw
  } catch {
    return raw
  }
}
/** Keep the old receive path alive while all new sends wait for confirmed ownership. */
export class SilentSocketMigration {
  private candidate: WebSocket | null = null
  private old: WebSocket | null = null
  private queued: { frame: string; resolve: () => void; reject: (error: Error) => void }[] = []
  private retry: ReturnType<typeof setTimeout> | null = null
  private attempts = 0
  constructor(private callbacks: MigrationCallbacks) {}
  get active() {
    return this.old !== null
  }
  canSend() {
    return this.active || this.callbacks.current()?.readyState === 1
  }
  send(frame: string) {
    if (this.active) {
      const sent = new Promise<void>((resolve, reject) => this.queued.push({ frame, resolve, reject }))
      // Most actions are fire-and-forget; callers waiting on actual transmission still receive failures.
      void sent.catch(() => {})

      return sent
    }
    const socket = this.callbacks.current()
    if (!socket || socket.readyState !== 1) throw new Error('WebSocket is not open')
    socket.send(frame)

    return Promise.resolve()
  }
  begin(socket: WebSocket) {
    if (this.active || socket !== this.callbacks.current() || socket.readyState !== 1) return
    this.old = socket
    socket.send(JSON.stringify({ update: 'ack' }))
    this.connect()
  }
  private connect() {
    if (!this.active) return
    let socket: WebSocket
    try {
      socket = this.callbacks.open()
    } catch {
      this.scheduleRetry()

      return
    }
    this.candidate = socket
    const auth = this.callbacks.auth()
    const frames: any[] = []
    let authenticated = false
    let historyReady = false
    socket.onopen = () => {
      if (socket === this.candidate) socket.send(JSON.stringify({ ...auth, update: 'waiting' }))
    }
    socket.onmessage = event => {
      if (socket !== this.candidate) return
      const data = parseSocketFrame(String(event.data))
      if (data.type === 'session_closed' && data.session_id === auth.session_id) {
        const previous = this.old!
        // Closure is terminal: queued actions must never spill into a new conversation.
        previous.onclose = null
        previous.onmessage = null
        this.cancel()
        previous.close()
        this.callbacks.receive(data)

        return
      }
      if (data.type === 'auth_error' || data.type === 'auth_failed') {
        this.callbacks.failed(data.message || 'Replacement connection authentication failed')
        socket.close()

        return
      }
      if (data.type === 'auth_success') {
        if (data.sessionId !== auth.session_id) {
          this.callbacks.failed('Replacement connection changed the conversation')
          socket.close()

          return
        }
        authenticated = true
        frames.push(data)
        socket.send(JSON.stringify({ update: 'waiting' }))

        return
      }
      if (data.update === 'done') {
        if (!authenticated || !historyReady) {
          socket.close()

          return
        }
        const previous = this.old!
        this.callbacks.promote(socket)
        // Preserve local drafts and optimistic messages while refreshing persisted history.
        for (const frame of frames)
          this.callbacks.receive(
            frame.type === 'message_history' || frame.type === 'auth_success' ? { ...frame, migration: true } : frame
          )
        socket.onmessage = event => {
          if (socket === this.callbacks.current()) this.callbacks.receive(parseSocketFrame(String(event.data)))
        }
        try {
          while (this.queued.length) {
            const pending = this.queued[0]
            socket.send(pending.frame)
            this.queued.shift()
            pending.resolve()
          }
        } catch {
          socket.close()
          this.candidate = null
          this.scheduleRetry()

          return
        }
        this.old = null
        this.candidate = null
        this.attempts = 0
        previous.onclose = null
        previous.onmessage = null
        previous.close()

        return
      }
      if (data.type === 'message_history') historyReady = true
      // A draining replacement is retried through ingress, without retiring the original socket.
      if (data.update === 'ready') {
        socket.close()

        return
      }
      frames.push(data)
    }
    socket.onerror = () => {
      if (socket === this.candidate) socket.close()
    }
    socket.onclose = () => {
      if (socket !== this.candidate) return
      this.candidate = null
      this.scheduleRetry()
    }
  }
  private scheduleRetry() {
    if (!this.active || this.retry) return
    this.retry = setTimeout(
      () => {
        this.retry = null
        this.connect()
      },
      Math.min(1000 * ++this.attempts, 5000)
    )
  }
  cancel() {
    if (this.retry) clearTimeout(this.retry)
    this.retry = null
    const candidate = this.candidate
    this.candidate = null
    this.old = null
    for (const pending of this.queued.splice(0))
      pending.reject(new Error('Connection changed before queued action was sent'))
    if (candidate) {
      candidate.onclose = null
      candidate.close()
    }
  }
}
